import {
  Typography as MuiTypography,
  IconButton as MuiIconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { Cancel as MuiCancelIcon } from "@mui/icons-material";
import { Trans } from "react-i18next";
import { DaysOfTheWeek } from "app/shared/constants";
import { convertDateStringToLocalDatetime } from "app/shared/utils";
import { headerStyles, multiLineCell } from "app/shared/UI/sharedStyles";

const getDaysOfWeekString = (arraySet) =>
  arraySet.map((days, idx) => (idx ? ", " : "") + days.substring(0, 3));

const sortedArrayOfDaysOfWeek = (data) => {
  let sortedData = [];
  sortedData = data.sort((a, b) => {
    return DaysOfTheWeek.indexOf(a) - DaysOfTheWeek.indexOf(b);
  });

  return getDaysOfWeekString(sortedData);
};

const timeConvert = (time) => {
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1); // Remove full string match value
    time[5] = +time[0] < 12 ? "AM" : "PM"; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
  }
  return time.join(""); // return adjusted time or original string
};

export const defaultTableColumnHeaders = (
  t,
  handleClickCancelRecurring,
  setCancelRecurringOutreachRow,
) => [
  {
    field: "startDate",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnStartDate" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {convertDateStringToLocalDatetime(row.scheduleSummary.startDate)
          ? convertDateStringToLocalDatetime(
              row.scheduleSummary.startDate,
            ).toLocaleDateString()
          : null}
      </MuiTypography>
    ),
    minWidth: 110,
    sortable: false,
    flex: 1,
  },
  {
    field: "status",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnStatus" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {row.scheduleSummary.status}
        {row.scheduleSummary.status === "PAUSED" && row.importRequest?.studyTeamOutreachResumeDate && (
          <>
            <br />
            Resume - {convertDateStringToLocalDatetime(row.importRequest.studyTeamOutreachResumeDate)?.toLocaleDateString()}
          </>
        )}
      </MuiTypography>
    ),
    sortable: false,
    minWidth: 155,
    flex: 1,
  },
  {
    field: "generationType",
    renderHeader: () => {
      return (
        <MuiTypography style={headerStyles}>
          <Trans i18nKey="ConductOutreachPage.tableColumnMethod" />
        </MuiTypography>
      );
    },
    renderCell: (params) => {
      if (params.value === "HONEST_BROKER_GENERATED") {
        return (
          <MuiTooltip
            title={t("ConductOutreachPage.tooltipHonestBroker")}
            arrow
          >
            <MuiTypography sx={multiLineCell}>
              {t("ConductOutreachPage.rowGenerationTypeHonestBroker")}
            </MuiTypography>
          </MuiTooltip>
        );
      }
      if (params.value === "STUDY_TEAM_GENERATED") {
        return (
          <MuiTooltip title={t("ConductOutreachPage.tooltipSelfService")} arrow>
            <MuiTypography sx={multiLineCell}>
              {t("ConductOutreachPage.rowGenerationTypeSelfService")}
            </MuiTypography>
          </MuiTooltip>
        );
      }
    },
    minWidth: 80,
    flex: 1,
    sortable: false,
  },
  {
    field: "conductedBy",
    renderHeader: () => {
      return (
        <MuiTypography style={headerStyles}>
          <Trans i18nKey="ConductOutreachPage.tableColumnInitiator" />
        </MuiTypography>
      );
    },
    renderCell: (row) => {
      return (
        <MuiTypography sx={multiLineCell}>
          {row.value?.displayName}
        </MuiTypography>
      );
    },
    minWidth: 160,
    flex: 1,
  },
  {
    field: "waveName",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnWaveName" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    minWidth: 180,
    flex: 1,
    sortable: false,
  },
  {
    field: "filename",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnCohortSource" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {row.importRequest.filename}
      </MuiTypography>
    ),
    sortable: false,
    minWidth: 300,
    flex: 1,
  },
  {
    field: "channel",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnCohortChannel" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    sortable: false,
    minWidth: 150,
    flex: 1,
  },
  {
    field: "quantity",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnQuantity" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    sortable: false,
    align: "center",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "recurringDays",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnDays" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {sortedArrayOfDaysOfWeek(row.scheduleSummary.recurringDays)}
      </MuiTypography>
    ),
    sortable: false,
    minWidth: 200,
    flex: 1,
  },
  {
    field: "recurringTime",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnTime" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {timeConvert(row.scheduleSummary.recurringTime)}
      </MuiTypography>
    ),
    sortable: false,
    minWidth: 110,
    flex: 1,
  },
  {
    field: "endDate",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.recurringTableColumnEndDate" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {convertDateStringToLocalDatetime(row.scheduleSummary.endDate)
          ? convertDateStringToLocalDatetime(
              row.scheduleSummary.endDate,
            ).toLocaleDateString()
          : null}
      </MuiTypography>
    ),
    sortable: false,
    minWidth: 110,
    flex: 1,
  },
  {
    field: "note",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnNote" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    sortable: false,
    minWidth: 250,
    flex: 1,
  },
  {
    field: "actions",
    headerAlign: "left",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="ConductOutreachPage.tableColumnActions" />
      </MuiTypography>
    ),
    type: "actions",
    align: "left",
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      if (params.row.generationType === "HONEST_BROKER_GENERATED") {
        const { row } = params;
        return (
          <MuiTooltip
            title={t("ConductOutreachPage.rowTooltipRecurringCancelIcon")}
            arrow
          >
            <MuiIconButton
              size="small"
              onClick={() => {
                handleClickCancelRecurring();
                setCancelRecurringOutreachRow(row);
              }}
            >
              <MuiCancelIcon fontSize="small" />
            </MuiIconButton>
          </MuiTooltip>
        );
      }
    },
  },
];
